/** @jsx jsx */
import { Children, forwardRef, Fragment, memo } from 'react';
import { css, jsx } from '@emotion/react';
const flexAlignItemsMap = {
  center: css({
    alignItems: 'center'
  }),
  baseline: css({
    alignItems: 'baseline'
  }),
  flexStart: css({
    alignItems: 'flex-start'
  }),
  flexEnd: css({
    alignItems: 'flex-end'
  }),
  start: css({
    alignItems: 'start'
  }),
  end: css({
    alignItems: 'end'
  })
};
const flexJustifyContentMap = {
  center: css({
    justifyContent: 'center'
  }),
  flexStart: css({
    justifyContent: 'flex-start'
  }),
  flexEnd: css({
    justifyContent: 'flex-end'
  }),
  start: css({
    justifyContent: 'start'
  }),
  end: css({
    justifyContent: 'end'
  })
};
const flexWrapMap = {
  wrap: css({
    flexWrap: 'wrap'
  })
};
const baseStyles = css({
  display: 'flex',
  boxSizing: 'border-box',
  flexDirection: 'row'
});
const dividerStyles = css({
  margin: '0 -2px',
  color: "var(--ds-text-subtle, #42526E)",
  pointerEvents: 'none'
});

const Divider = ({
  children
}) => jsx("span", {
  css: dividerStyles
}, children);
/**
 * __Inline__
 *
 * Inline is a primitive component based on flexbox that manages the horizontal layout of direct children.
 * Renders a `div` by default.
 *
 * @example
 * ```tsx
 * const App = () => (
 *   <Inline gap="space.100">
 *     <Button />
 *     <Button />
 *   </Inline>
 * )
 * ```
 */


const Inline = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  gap,
  alignItems,
  justifyContent,
  flexWrap,
  divider,
  UNSAFE_style,
  children,
  testId
}, ref) => {
  const dividerComponent = typeof divider === 'string' ? jsx(Divider, null, divider) : divider;
  const childrenArray = Children.toArray(children).filter(Boolean);
  return jsx("div", {
    style: UNSAFE_style,
    css: [baseStyles, gap && columnGapMap[gap], alignItems && flexAlignItemsMap[alignItems], justifyContent && flexJustifyContentMap[justifyContent], flexWrap && flexWrapMap[flexWrap]],
    "data-testid": testId,
    ref: ref
  }, childrenArray.map((child, index) => {
    return jsx(Fragment, {
      key: index
    }, divider && index > 0 ? dividerComponent : null, child);
  }));
}));
Inline.displayName = 'Inline';
export default Inline;
/**
 * THIS SECTION WAS CREATED VIA CODEGEN DO NOT MODIFY {@see http://go/af-codegen}
 * @codegen <<SignedSource::2dd7c6b8450b1bbd1fb8fabf12fffa33>>
 * @codegenId spacing
 * @codegenCommand yarn codegen-styles
 * @codegenParams ["columnGap"]
 * @codegenDependency ../../../tokens/src/artifacts/tokens-raw/atlassian-light.tsx <<SignedSource::07ef29d58a2b23af8b098515466d7e22>>
 */

const columnGapMap = {
  'scale.0': css({
    columnGap: "var(--ds-scale-0, 0px)"
  }),
  'scale.025': css({
    columnGap: "var(--ds-scale-025, 2px)"
  }),
  'scale.050': css({
    columnGap: "var(--ds-scale-050, 4px)"
  }),
  'scale.075': css({
    columnGap: "var(--ds-scale-075, 6px)"
  }),
  'scale.100': css({
    columnGap: "var(--ds-scale-100, 8px)"
  }),
  'scale.150': css({
    columnGap: "var(--ds-scale-150, 12px)"
  }),
  'scale.200': css({
    columnGap: "var(--ds-scale-200, 16px)"
  }),
  'scale.250': css({
    columnGap: "var(--ds-scale-250, 20px)"
  }),
  'scale.300': css({
    columnGap: "var(--ds-scale-300, 24px)"
  }),
  'scale.400': css({
    columnGap: "var(--ds-scale-400, 32px)"
  }),
  'scale.500': css({
    columnGap: "var(--ds-scale-500, 40px)"
  }),
  'scale.600': css({
    columnGap: "var(--ds-scale-600, 48px)"
  })
};
/**
 * @codegenEnd
 */